import { useModal, useToast } from '@chillibean/component-library';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import useWindowSize from '../../../../../hook/useWindowSize';
import { FooterButtons } from '../../components/container/FooterButtons';
import { DisplayNameType, useDisplayName, useStudyScheduler } from '../../hooks';
import { addStudyEventProcedure, removeStudyEventProcedure } from '../../redux/actions';
import { StudyProcedureDefinitionModal, StudyEventDefinitionModal } from './components/container';
import { StudySchedulerHeader, StudySchedulerTable } from './components/presentational';

export function StudySchedulerPage() {
  // HOOKS
  // React
  const tableContainerRef = useRef(null);
  const [tableContainerWidth, setTableContainerWidth] = useState(1560);
  // Store
  const dispatch = useDispatch();
  const { data: protocol, loading } = useSelector((state) => state.protocolReducer.protocol);
  const { table: tableData } = protocol || {};
  // Custom
  const { getDisplayName } = useDisplayName();
  const {
    closeModal: closeStudyEventDefinitionModal,
    data: selectedStudyEventDefinition,
    modalOpen: studyEventDefinitionModalOpen,
    openModal: openStudyEventDefinitionModal,
  } = useModal();
  const {
    closeModal: closeStudyProcedureDefinitionModal,
    data: selectedStudyProcedureDefinition,
    modalOpen: studyProcedureDefinitionModalOpen,
    openModal: openStudyProcedureDefinitionModal,
  } = useModal();
  const { showErrorToast } = useToast();
  const {
    numberOfExtraCells,
    selectAllActiveCells,
    selectAllDisabled,
    selectAllLoading,
    unSelectAllLoading,
    unselectAllActiveCells,
  } = useStudyScheduler({
    dimensionWidth: tableContainerWidth,
  });

  const resizeListener = () => {
    setTableContainerWidth(tableContainerRef?.current?.offsetWidth);
  };

  useEffect(() => {
    if (!tableContainerRef?.current) return;

    resizeListener();
  }, [tableContainerRef?.current]);

  useWindowSize({ resizeFunction: resizeListener });

  const handleAddStudyEventProcedure = (data) => {
    dispatch(
      addStudyEventProcedure({
        onError: showErrorToast,
        protocolId: protocol.id,
        studyEventProcedureData: data,
      }),
    );
  };

  const handleRemoveStudyEventProcedure = (data) => {
    dispatch(
      removeStudyEventProcedure({
        onError: showErrorToast,
        protocolId: protocol.id,
        studyEventProcedureData: data,
      }),
    );
  };

  return (
    <>
      <StudyEventDefinitionModal
        onClose={closeStudyEventDefinitionModal}
        onSuccess={closeStudyEventDefinitionModal}
        studyEventDefinition={selectedStudyEventDefinition}
        visible={studyEventDefinitionModalOpen}
      />
      <StudyProcedureDefinitionModal
        onClose={closeStudyProcedureDefinitionModal}
        onSuccess={closeStudyProcedureDefinitionModal}
        studyProcedureDefinition={selectedStudyProcedureDefinition}
        visible={studyProcedureDefinitionModalOpen}
      />
      <StudySchedulerHeader
        loading={loading}
        openStudyEventDefinitionModal={() => openStudyEventDefinitionModal()}
        openStudyProcedureDefinitionModal={() => openStudyProcedureDefinitionModal()}
        selectAllParams={{
          disabled: !tableData?.studyProcedureDefinitions.length || !tableData?.studyEventDefinitions.length,
          isAllSelected: selectAllDisabled,
          select: { handleSelectAll: () => selectAllActiveCells(), selectAllLoading: selectAllLoading },
          unselect: { handleUnselectAll: () => unselectAllActiveCells(), unselectAllLoading: unSelectAllLoading },
        }}
      />
      {Boolean(protocol) && (
        <TableContainer ref={tableContainerRef}>
          <StudySchedulerTable
            addStudyEventProcedure={handleAddStudyEventProcedure}
            columnTag={getDisplayName(DisplayNameType.STUDY_EVENT).humanized.plural}
            numberOfExtraCells={numberOfExtraCells}
            openStudyEventDefinitionModal={(studyEventDefinition) =>
              openStudyEventDefinitionModal(studyEventDefinition)
            }
            openStudyProcedureDefinitionModal={(studyProcedureDefinition) =>
              openStudyProcedureDefinitionModal(studyProcedureDefinition)
            }
            removeStudyEventProcedure={handleRemoveStudyEventProcedure}
            rowTag={getDisplayName(DisplayNameType.STUDY_PROCEDURE).humanized.plural}
            tableData={tableData || {}}
          />
        </TableContainer>
      )}
      <FooterButtons />
    </>
  );
}

const TableContainer = styled.div`
  margin: 0 -60px 0 -32px;
`;

export default StudySchedulerPage;
